import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUploadItemImages } from '@/hooks/useUserItems';
import type { UserItem } from '@/types/userItem';

export type RapidPhotoResult = 'captured' | 'skipped' | 'nocard';

interface Args {
  items: UserItem[];
  open: boolean;
}

/**
 * Guided rapid-photograph queue over items that lack REAL photos
 * (a stock_image_url still counts as photoless — we want the seller to
 * capture the physical card).
 *
 * Autosaves via useUploadItemImages, tracks per-item outcome, and exposes
 * a session summary. Session state is memory-only.
 */
export function useRapidPhotoQueue({ items, open }: Args) {
  const queryClient = useQueryClient();
  const uploadImages = useUploadItemImages();

  // Freeze the queue on open so items don't shuffle as we save/advance.
  const [frozenIds, setFrozenIds] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<Record<string, RapidPhotoResult>>({});
  const [pending, setPending] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setFrozenIds(items.map((i) => i.id));
      setIndex(0);
      setResults({});
      setPending([]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const queue = useMemo(() => {
    if (!frozenIds.length) return [] as UserItem[];
    const byId = new Map(items.map((i) => [i.id, i]));
    return frozenIds.map((id) => byId.get(id)).filter(Boolean) as UserItem[];
  }, [frozenIds, items]);

  const current = queue[index];

  const addFiles = useCallback((files: File[]) => {
    if (!files.length) return;
    setPending((prev) => [...prev, ...files].slice(0, 10));
  }, []);

  const removePending = useCallback((i: number) => {
    setPending((prev) => prev.filter((_, idx) => idx !== i));
  }, []);

  const advance = useCallback(() => {
    setPending([]);
    setIndex((i) => Math.min(queue.length, i + 1));
  }, [queue.length]);

  const skip = useCallback(() => {
    if (current) setResults((r) => ({ ...r, [current.id]: 'skipped' }));
    advance();
  }, [current, advance]);

  const noCard = useCallback(() => {
    if (current) setResults((r) => ({ ...r, [current.id]: 'nocard' }));
    advance();
  }, [current, advance]);

  const saveAndNext = useCallback(async () => {
    if (!current || pending.length === 0 || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const { processSelectedFiles } = await import('@/lib/photoInputUtils');
      const files = await processSelectedFiles(pending, { maxCount: 10 });
      if (files.length === 0) {
        setSaving(false);
        savingRef.current = false;
        return;
      }
      await uploadImages.mutateAsync({ itemId: current.id, files });
      setResults((r) => ({ ...r, [current.id]: 'captured' }));
      await queryClient.invalidateQueries({ queryKey: ['my-items'] });
      advance();
    } catch (err) {
      console.error('Rapid photo save failed:', err);
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  }, [current, pending, uploadImages, queryClient, advance]);

  const finish = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['my-items'] });
  }, [queryClient]);

  const summary = {
    captured: Object.values(results).filter((r) => r === 'captured').length,
    skipped: Object.values(results).filter((r) => r === 'skipped').length,
    nocard: Object.values(results).filter((r) => r === 'nocard').length,
  };

  return {
    total: queue.length,
    index,
    current,
    pending,
    saving,
    isDone: index >= queue.length,
    summary,
    addFiles,
    removePending,
    skip,
    noCard,
    saveAndNext,
    finish,
  };
}
